// HHL's own stock, counted one way everywhere it's shown or used. On hand is everything on the shelf; available is
// what's free to sell or build with: Shopify's available (on hand less the units on open orders that haven't shipped,
// `committed`, and anything else Shopify holds back: damaged, safety stock, quality control) plus the ledger's
// balance (local items have no orders). A CM's stock is never in it (consignment's view).
import type { Po, PoLine, StockLevel } from "./db.gen.ts";
import { isOpen } from "./po.ts";

/**
 * Every count Shopify keeps (migration 062; Mitch's inventory manager, 2026-11-03: "all shopify inventory counts,
 * available, and the other categorizations"): on hand = available + committed + what it holds back (damaged, quality
 * control, safety stock, reserved). Local items (the ledger) are on hand and available alike.
 */
export type HhlStock = {
  onHand: number;
  available: number;
  committed: number;
  damaged: number;
  qualityControl: number;
  safetyStock: number;
  reserved: number;
};
type Held = Partial<Pick<StockLevel, "damaged" | "quality_control" | "safety_stock" | "reserved">>;

type Loc = { id: string; kind: string; active: boolean; ignored: boolean };

/** Per item, at HHL's locations in use. */
export function hhlStock(
  levels: readonly (Pick<StockLevel, "item_id" | "location_id" | "on_hand" | "available" | "committed"> & Held)[],
  balances: readonly { item_id: string; location_id: string; qty: number | string }[],
  locations: readonly Loc[],
): Map<string, HhlStock> {
  const hhl = new Set(locations.filter((l) => l.kind === "hhl" && l.active && !l.ignored).map((l) => l.id));
  const out = new Map<string, HhlStock>();
  const add = (item: string, n: Partial<HhlStock>) => {
    const s = out.get(item) ?? { onHand: 0, available: 0, committed: 0, damaged: 0, qualityControl: 0, safetyStock: 0, reserved: 0 };
    for (const k of Object.keys(n) as (keyof HhlStock)[]) s[k] += n[k] ?? 0;
    out.set(item, s);
  };
  for (const s of levels)
    if (hhl.has(s.location_id))
      add(s.item_id, {
        onHand: Number(s.on_hand),
        available: Number(s.available),
        committed: Number(s.committed),
        damaged: Number(s.damaged ?? 0),
        qualityControl: Number(s.quality_control ?? 0),
        safetyStock: Number(s.safety_stock ?? 0),
        reserved: Number(s.reserved ?? 0),
      });
  for (const b of balances) if (hhl.has(b.location_id)) add(b.item_id, { onHand: Number(b.qty), available: Number(b.qty) });
  return out;
}

/** Why available isn't on hand, or null when they're the same: "135 on hand · 15 committed to customer orders" (Shopify's
 * word; "open orders" reads as POs on the purchasing screens). */
export function stockNote(
  // (A plan result is one too: its `reserved` is pre-sales' reservations, not Shopify's count, so it reads as none.)
  s: (Pick<HhlStock, "onHand" | "available" | "committed"> & Partial<Omit<HhlStock, "reserved">> & { reserved?: unknown }) | undefined,
): string | null {
  if (!s || s.available === s.onHand) return null;
  const fmt = (n: number) => n.toLocaleString("en-US");
  // What Shopify holds back by name; a gap it doesn't name (counts from before migration 062) as one.
  const { damaged = 0, qualityControl = 0, safetyStock = 0 } = s;
  const reserved = typeof s.reserved === "number" ? s.reserved : 0;
  const named = damaged + qualityControl + safetyStock + reserved;
  const other = s.onHand - s.available - s.committed - named;
  const parts = [
    s.committed > 0 && `${fmt(s.committed)} committed to customer orders`,
    damaged > 0 && `${fmt(damaged)} damaged`,
    qualityControl > 0 && `${fmt(qualityControl)} in quality control`,
    safetyStock > 0 && `${fmt(safetyStock)} safety stock`,
    reserved > 0 && `${fmt(reserved)} reserved`,
    other > 0 && `${fmt(other)} held back in Shopify`,
  ].filter(Boolean);
  return `${fmt(s.onHand)} on hand${parts.length ? ` · ${parts.join(" · ")}` : ""}${s.available < 0 ? ` · oversold by ${fmt(-s.available)}` : ""}`;
}

/** Units on their way to HHL: on POs the vendor has (sent, not closed), and on drafts not sent yet. */
export type Incoming = { incoming: number; drafted: number; pos: { number: string; qty: number; draft: boolean }[] };

/**
 * Per item, what's still to arrive at HHL (Mitch's inventory manager: "I want to see the number I ordered, as well as in
 * stock"): each open PO line's quantity less what's been received, for POs shipping to HHL (or nowhere set). Pre-sales
 * --- a make PO's units for backers go straight to them, and a share closed with "Nothing else is coming" brings none.
 */
export function incomingByItem(
  pos: readonly Pick<Po, "id" | "number" | "status" | "ship_to_location_id" | "hhl_share_closed">[],
  lines: readonly Pick<PoLine, "po_id" | "item_id" | "qty" | "qty_received" | "qty_direct">[],
  locations: readonly Loc[],
): Map<string, Incoming> {
  const hhl = new Set(locations.filter((l) => l.kind === "hhl").map((l) => l.id));
  const open = new Map(
    pos.filter((p) => isOpen(p.status) && !p.hhl_share_closed && (!p.ship_to_location_id || hhl.has(p.ship_to_location_id))).map((p) => [p.id, p]),
  );
  const out = new Map<string, Incoming>();
  for (const l of lines) {
    const po = open.get(l.po_id);
    const left = po ? Number(l.qty) - Number(l.qty_direct ?? 0) - Number(l.qty_received) : 0;
    if (!po || left <= 0) continue;
    const s = out.get(l.item_id) ?? { incoming: 0, drafted: 0, pos: [] };
    const draft = po.status === "draft";
    if (draft) s.drafted += left;
    else s.incoming += left;
    s.pos.push({ number: po.number, qty: left, draft });
    out.set(l.item_id, s);
  }
  return out;
}
